import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  requestPush,
  isPushConfigured,
  getPushConfigProblem,
  normalizeNotificationSettings,
  emitNotificationSettingsChanged,
  type NotificationSettingsSnapshot,
} from '../services/notificationService';
import { FilterPreset, RidesQuery } from '../types';
import {
  deleteFilterPreset,
  fetchFilterPresets,
  updateFilterPreset,
} from '../services/filterPresetService';
import { useAuth } from '../contexts/AuthContext';

type SettingsTab = 'profile' | 'preferences' | 'notifications' | 'security';
const LAST_FILTER_PRESET_KEY = 'rides_last_filter_preset_id';

type NotificationSettings = NotificationSettingsSnapshot;

const defaultNotifications: NotificationSettings = normalizeNotificationSettings();

interface SetPasswordFormProps {
  onSuccess: () => void;
  setMessage: (msg: string) => void;
}

const SetPasswordForm: React.FC<SetPasswordFormProps> = ({ onSuccess, setMessage }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (newPassword.length < 6) {
      setMessage('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/auth/set-password', { password: newPassword });
      setMessage('Password set successfully. You can now log in with email & password.');
      onSuccess();
    } catch (err: any) {
      setMessage(err.response?.data?.message || 'Failed to set password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: 520 }}>
      <TextField
        type="password"
        label="New password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        helperText="Minimum 6 characters"
      />
      <TextField
        type="password"
        label="Confirm password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={confirmPassword.length > 0 && newPassword !== confirmPassword}
        helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
      />
      <Box>
        <Button variant="contained" disabled={saving} onClick={handleSubmit}>Set password</Button>
      </Box>
    </Stack>
  );
};

const Settings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = useMemo(() => {
    const tab = new URLSearchParams(location.search).get('tab') as SettingsTab | null;
    const allowedTabs: SettingsTab[] = ['profile', 'preferences', 'notifications', 'security'];
    return tab && allowedTabs.includes(tab) ? tab : 'profile';
  }, [location.search]);

  const [tab, setTab] = useState<SettingsTab>(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [profile, setProfile] = useState({ name: '', email: '', phone: '', isOnline: false, fcmToken: '' });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '' });

  // ── Security extras ──────────────────────────────────────────────────────
  const [twoFAQR, setTwoFAQR] = useState<string | null>(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFASaving, setTwoFASaving] = useState(false);
  const [googleLinking, setGoogleLinking] = useState(false);
  const [filterPresets, setFilterPresets] = useState<FilterPreset[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<FilterPreset | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FilterPreset | null>(null);
  const [deletingPreset, setDeletingPreset] = useState(false);
  const [notifications, setNotifications] = useState<NotificationSettings>(defaultNotifications);
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    // Handle redirect back from Google link flow
    const params = new URLSearchParams(location.search);
    if (params.get('googleLinked') === 'true') {
      setMessage('Google account linked successfully.');
      navigate('/settings?tab=security', { replace: true });
      // Refresh user data to pick up new googleId
      api.get('/api/auth/verify').then((res) => updateUser(res.data.user)).catch(() => {});
    }

    setPresetsLoading(true);
    fetchFilterPresets()
      .then(setFilterPresets)
      .catch(() => setFilterPresets([]))
      .finally(() => setPresetsLoading(false));

    Promise.all([
      api.get('/api/users/profile'),
      api.get('/api/settings/notifications').catch(() => ({ data: defaultNotifications })),
    ])
      .then(([profileResponse, notificationResponse]) => {
        setProfile({
          name: profileResponse.data.name || '',
          email: profileResponse.data.email || '',
          phone: profileResponse.data.phone || '',
          isOnline: Boolean(profileResponse.data.isOnline),
          fcmToken: profileResponse.data.fcmToken || '',
        });
        setNotifications(normalizeNotificationSettings(notificationResponse.data));
      })
      .catch(() => setMessage('Unable to load settings.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeTab = (_: React.SyntheticEvent, next: SettingsTab) => {
    setTab(next);
    navigate(`/settings?tab=${next}`, { replace: true });
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const response = await api.put('/api/users/profile', {
        name: profile.name,
        ...(profile.phone ? { phone: profile.phone } : {}),
      });
      setProfile((prev) => ({ ...prev, ...response.data.user }));
      setMessage('Profile saved.');
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Unable to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const toggleOnline = async (next: boolean) => {
    setProfile((prev) => ({ ...prev, isOnline: next }));
    try {
      await api.patch('/api/users/online', { isOnline: next });
    } catch {
      setProfile((prev) => ({ ...prev, isOnline: !next }));
      setMessage('Unable to update online status.');
    }
  };

  const savePassword = async () => {
    if (!passwords.currentPassword || !passwords.newPassword) {
      setMessage('Enter current and new password.');
      return;
    }
    setSaving(true);
    try {
      await api.put('/api/users/password', passwords);
      setPasswords({ currentPassword: '', newPassword: '' });
      setMessage('Password updated.');
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Unable to update password.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePreset = (preset: FilterPreset) => {
    setDeleteTarget(preset);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingPreset) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleConfirmDeletePreset = async () => {
    if (!deleteTarget) return;
    setDeletingPreset(true);
    try {
      await deleteFilterPreset(deleteTarget.id);
      setFilterPresets((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      try {
        if (window.localStorage.getItem(LAST_FILTER_PRESET_KEY) === deleteTarget.id) {
          window.localStorage.removeItem(LAST_FILTER_PRESET_KEY);
        }
      } catch {
        // localStorage is best-effort only.
      }
      setMessage('Filter deleted.');
    } catch {
      setMessage('Could not delete filter.');
    } finally {
      setDeletingPreset(false);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleRenamePreset = (preset: FilterPreset) => {
    setRenameTarget(preset);
    setRenameValue(preset.name);
    setRenameDialogOpen(true);
  };

  const handleConfirmRename = async () => {
    const name = renameValue.trim();
    if (!renameTarget || !name) return;
    setRenameDialogOpen(false);
    try {
      const updated = await updateFilterPreset(renameTarget.id, { name });
      setFilterPresets((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setMessage('Filter renamed.');
    } catch {
      setMessage('Could not rename filter.');
    }
  };

  const summariseQuery = (query: Partial<RidesQuery>): string => {
    const parts: string[] = [];
    if (query.vehicleTypes?.length) parts.push(`Vehicle: ${query.vehicleTypes.join(', ')}`);
    if (query.dutyTypes?.length) parts.push(`Duty: ${query.dutyTypes.join(', ')}`);
    if (query.pickupAreas?.length) parts.push(`From: ${query.pickupAreas.join(', ')}`);
    if (query.dropAreas?.length) parts.push(`To: ${query.dropAreas.join(', ')}`);
    if (query.priorities?.length) parts.push(`Priority: ${query.priorities.join(', ')}`);
    if (query.search) parts.push(`Search: ${query.search}`);
    if (query.urgentOnly) parts.push('Urgent only');
    return parts.length ? parts.join(' · ') : 'No filters set';
  };

  const ridesUrlForQuery = (query: Partial<RidesQuery>, presetId?: string) => {
    const params = new URLSearchParams();
    if (presetId) params.set('filterPresetId', presetId);
    if (query.vehicleTypes?.length) params.set('vehicleTypes', query.vehicleTypes.join(','));
    if (query.dutyTypes?.length) params.set('dutyTypes', query.dutyTypes.join(','));
    if (query.priorities?.length) params.set('priorities', query.priorities.join(','));
    if (query.pickupAreas?.length) params.set('pickupAreas', query.pickupAreas.join(','));
    if (query.dropAreas?.length) params.set('dropAreas', query.dropAreas.join(','));
    if (query.search) params.set('search', query.search);
    if (query.urgentOnly) params.set('urgentOnly', 'true');
    const queryString = params.toString();
    return `/app/rides${queryString ? `?${queryString}` : ''}`;
  };

  const saveNotifications = async (
    nextSettings: NotificationSettings = notifications,
    successMessage = 'Notification settings saved.'
  ) => {
    setSaving(true);
    setNotifications(nextSettings);
    try {
      const response = await api.put('/api/settings/notifications', nextSettings);
      const saved = normalizeNotificationSettings(response.data.notifications ?? response.data);
      setNotifications(saved);
      emitNotificationSettingsChanged(saved);
      setMessage(successMessage);
    } catch {
      setMessage('Unable to save notifications.');
    } finally {
      setSaving(false);
    }
  };

  const [pushBusy, setPushBusy] = useState(false);

  const handlePushToggle = async (enable: boolean) => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (enable) {
        if (!isPushConfigured()) {
          setMessage(getPushConfigProblem() || 'Push notifications are not configured for this deployment. Contact support.');
          return;
        }
        const result = await requestPush();
        setPermission(typeof Notification !== 'undefined' ? Notification.permission : 'default');
        if (result.ok) {
          setProfile((prev) => ({ ...prev, fcmToken: result.token }));
          // Update local state + auto-save pushNotifications preference immediately
          const updated = { ...notifications, pushNotifications: true };
          await saveNotifications(updated, 'Push notifications enabled on this browser.');
        } else {
          setMessage(result.message);
        }
      } else {
        // Clear FCM token so server stops sending pushes to this browser
        await api.put('/api/users/profile', { fcmToken: null }).catch(() => {});
        setProfile((prev) => ({ ...prev, fcmToken: '' }));
        const updated = { ...notifications, pushNotifications: false };
        await saveNotifications(updated, 'Push notifications disabled for this browser.');
      }
    } finally {
      setPushBusy(false);
    }
  };

  const enablePush = () => handlePushToggle(true);

  const handleMasterNotificationToggle = async (enabled: boolean) => {
    const updated = enabled
      ? { ...notifications, enabled }
      : { ...notifications, enabled, pushNotifications: false };

    if (!enabled) {
      await api.put('/api/users/profile', { fcmToken: null }).catch(() => {});
      setProfile((prev) => ({ ...prev, fcmToken: '' }));
    }

    await saveNotifications(
      updated,
      enabled ? 'Notifications enabled.' : 'Notifications disabled.'
    );
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 1080, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: 1 }}>
        <Tabs value={tab} onChange={changeTab} variant="scrollable" scrollButtons="auto">
          <Tab label="Profile" value="profile" />
          <Tab label="Saved Filters" value="preferences" />
          <Tab label="Notifications" value="notifications" />
          <Tab label="Security" value="security" />
        </Tabs>
      </Paper>

      {tab === 'profile' && (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="h6" fontWeight={900}>Profile</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>Keep your phone and availability current for support and account recovery.</Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Name" value={profile.name} onChange={(event) => setProfile((prev) => ({ ...prev, name: event.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" value={profile.email} disabled />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone" value={profile.phone} onChange={(event) => setProfile((prev) => ({ ...prev, phone: event.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={profile.isOnline} onChange={(event) => toggleOnline(event.target.checked)} color="success" />}
                label={profile.isOnline ? 'Online for rides' : 'Offline'}
              />
            </Grid>
          </Grid>
          <Button variant="contained" sx={{ mt: 3 }} disabled={saving} onClick={saveProfile}>Save profile</Button>
        </Paper>
      )}

      {tab === 'preferences' && (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
            <Box display="flex" alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={2} mb={2}>
              <Box>
                <Typography variant="h6" fontWeight={900}>My Saved Filters</Typography>
                <Typography variant="body2" color="text.secondary">
                  Rename, apply, and delete the filters saved from the Rides page.
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => navigate('/app/rides')}
                sx={{ flexShrink: 0 }}
              >
                New filter
              </Button>
            </Box>

            {presetsLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={24} />
              </Box>
            ) : filterPresets.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  px: 2,
                  borderRadius: 2,
                  border: '1.5px dashed',
                  borderColor: 'divider',
                  bgcolor: '#fafafa',
                }}
              >
                <Typography variant="body2" color="text.secondary" mb={1}>
                  No saved filters yet.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Go to Rides, choose pickup, drop, duty, vehicle or priority, then save it with a name.
                </Typography>
                <Box mt={2}>
                  <Button size="small" variant="outlined" onClick={() => navigate('/app/rides')}>
                    Go to Rides
                  </Button>
                </Box>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {filterPresets.map((preset) => (
                  <Paper
                    key={preset.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 2,
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: 'primary.main', bgcolor: '#f7f9ff' },
                    }}
                  >
                    <Box flex={1} minWidth={0}>
                      <Typography variant="subtitle2" fontWeight={800} noWrap>
                        {preset.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {summariseQuery(preset.query)}
                      </Typography>
                      {(preset.query.vehicleTypes?.length ||
                        preset.query.dutyTypes?.length ||
                        preset.query.pickupAreas?.length ||
                        preset.query.dropAreas?.length) ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={0.75}>
                          {preset.query.vehicleTypes?.map((vehicle) => (
                            <Chip key={`vehicle-${vehicle}`} label={vehicle} size="small" variant="outlined" sx={{ fontSize: 11, textTransform: 'capitalize' }} />
                          ))}
                          {preset.query.dutyTypes?.map((duty) => (
                            <Chip key={`duty-${duty}`} label={duty} size="small" color="secondary" variant="outlined" sx={{ fontSize: 11 }} />
                          ))}
                          {preset.query.pickupAreas?.map((city) => (
                            <Chip key={`from-${city}`} label={`From: ${city}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                          ))}
                          {preset.query.dropAreas?.map((city) => (
                            <Chip key={`to-${city}`} label={`To: ${city}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                          ))}
                        </Stack>
                      ) : null}
                    </Box>
                    <Stack direction="row" spacing={0.5} flexShrink={0}>
                      <Tooltip title="Apply this filter">
                        <IconButton size="small" color="primary" onClick={() => navigate(ridesUrlForQuery(preset.query, preset.id))}>
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rename">
                        <IconButton size="small" onClick={() => handleRenamePreset(preset)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDeletePreset(preset)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>

          <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>Rename filter</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="New name"
                value={renameValue}
                onChange={(event) => setRenameValue(event.target.value.slice(0, 40))}
                onKeyDown={(event) => event.key === 'Enter' && handleConfirmRename()}
                helperText={`${renameValue.length}/40 characters`}
                sx={{ mt: 1 }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" disabled={!renameValue.trim()} onClick={handleConfirmRename}>
                Rename
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={deleteDialogOpen}
            onClose={closeDeleteDialog}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Delete filter?</DialogTitle>
            <DialogContent>
              <Stack spacing={2} direction="row" alignItems="flex-start">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </Box>
                <Box minWidth={0}>
                  <Typography variant="body2" color="text.secondary">
                    This will remove
                    {' '}
                    <Box component="span" fontWeight={800} color="text.primary">
                      {deleteTarget?.name || 'this filter'}
                    </Box>
                    {' '}
                    from your saved filters on web and mobile.
                  </Typography>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button disabled={deletingPreset} onClick={closeDeleteDialog}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={deletingPreset}
                onClick={handleConfirmDeletePreset}
              >
                {deletingPreset ? 'Deleting...' : 'Delete filter'}
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      )}

      {tab === 'notifications' && (
        <Stack spacing={2.5}>
          <Alert severity={permission === 'granted' ? 'success' : permission === 'denied' ? 'warning' : 'info'}>
            {permission === 'granted'
              ? 'Browser notifications are allowed on this device.'
              : permission === 'denied'
              ? 'Browser notifications are blocked. Allow them in your browser site settings.'
              : 'Browser notifications are not yet enabled on this device.'}
          </Alert>

          {/* ── General ──────────────────────────────────────────────────── */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="h6" fontWeight={900} mb={1.5}>General</Typography>
            <Grid container spacing={0.5}>
              {/* Master toggle — always active */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.enabled}
                      onChange={(e) => handleMasterNotificationToggle(e.target.checked)}
                      disabled={saving}
                    />
                  }
                  label="Enable notifications"
                />
              </Grid>

              {/* Push notifications — per-device, triggers FCM register/deregister */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  disabled={!notifications.enabled || pushBusy || saving}
                  control={
                    <Switch
                      checked={notifications.pushNotifications}
                      onChange={(e) => handlePushToggle(e.target.checked)}
                    />
                  }
                  label={pushBusy ? 'Requesting permission…' : 'Push notifications (this browser)'}
                />
              </Grid>

              {/* Email + Sound — synced across devices */}
              {([
                { key: 'emailNotifications' as const, label: 'Email notifications' },
                { key: 'soundEnabled' as const, label: 'Sound' },
              ]).map(({ key, label }) => (
                <Grid item xs={12} sm={6} key={key}>
                  <FormControlLabel
                    disabled={!notifications.enabled || saving}
                    control={
                      <Switch
                        checked={notifications[key]}
                        onChange={(e) =>
                          saveNotifications(
                            { ...notifications, [key]: e.target.checked },
                            `${label} setting saved.`
                          )
                        }
                      />
                    }
                    label={label}
                  />
                </Grid>
              ))}
            </Grid>
            <Stack direction="row" spacing={1.5} mt={2} flexWrap="wrap">
              <Button variant="outlined" onClick={enablePush} disabled={!notifications.enabled || pushBusy || saving}>
                Enable push on this browser
              </Button>
              <Button variant="contained" onClick={() => saveNotifications()} disabled={saving}>
                Save settings
              </Button>
            </Stack>
          </Paper>

          {/* ── Ride alerts ──────────────────────────────────────────────── */}
          <Paper
            variant="outlined"
            sx={{ p: 2.5, opacity: notifications.enabled ? 1 : 0.45, pointerEvents: notifications.enabled ? 'auto' : 'none' }}
          >
            <Typography variant="h6" fontWeight={900} mb={1.5}>Ride Alerts</Typography>
            <Grid container spacing={0.5}>
              {([
                { key: 'newRide' as const, label: 'New ride alerts', sub: 'Notified when a new ride matches your area' },
                { key: 'rideUpdate' as const, label: 'Ride updates', sub: "Status changes on rides you've unlocked" },
                { key: 'rideCompletion' as const, label: 'Ride completion', sub: 'When a ride you unlocked is completed' },
              ]).map(({ key, label, sub }) => (
                <Grid item xs={12} sm={6} key={key}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notifications.rideAlerts[key]}
                        onChange={(e) =>
                          saveNotifications(
                            {
                              ...notifications,
                              rideAlerts: { ...notifications.rideAlerts, [key]: e.target.checked },
                            },
                            `${label} setting saved.`
                          )
                        }
                        disabled={saving}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{label}</Typography>
                        <Typography variant="caption" color="text.secondary">{sub}</Typography>
                      </Box>
                    }
                  />
                </Grid>
              ))}
            </Grid>
            <Box mt={1.5}>
              <Button variant="contained" onClick={() => saveNotifications()} disabled={saving} size="small">
                Save settings
              </Button>
            </Box>
          </Paper>

          {/* ── Quiet hours ───────────────────────────────────────────────── */}
          <Paper
            variant="outlined"
            sx={{ p: 2.5, opacity: notifications.enabled ? 1 : 0.45, pointerEvents: notifications.enabled ? 'auto' : 'none' }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={900}>Quiet hours</Typography>
              <Button startIcon={<AddIcon />} onClick={() => setNotifications((prev) => ({ ...prev, periods: [...prev.periods, { start: '22:00', end: '07:00', enabled: true }] }))}>
                Add window
              </Button>
            </Box>
            <Stack spacing={2}>
              {notifications.periods.length === 0 && (
                <Typography color="text.secondary" variant="body2">No quiet hours set. Notifications are sent any time.</Typography>
              )}
              {notifications.periods.map((period, index) => (
                <Stack key={`${period.start}-${index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                  <FormControlLabel
                    control={<Switch checked={period.enabled} onChange={(event) => {
                      const periods = [...notifications.periods];
                      periods[index] = { ...period, enabled: event.target.checked };
                      setNotifications((prev) => ({ ...prev, periods }));
                    }} />}
                    label="Active"
                  />
                  <TextField size="small" type="time" label="From" value={period.start} InputLabelProps={{ shrink: true }} onChange={(event) => {
                    const periods = [...notifications.periods];
                    periods[index] = { ...period, start: event.target.value };
                    setNotifications((prev) => ({ ...prev, periods }));
                  }} />
                  <TextField size="small" type="time" label="To" value={period.end} InputLabelProps={{ shrink: true }} onChange={(event) => {
                    const periods = [...notifications.periods];
                    periods[index] = { ...period, end: event.target.value };
                    setNotifications((prev) => ({ ...prev, periods }));
                  }} />
                  <IconButton color="error" onClick={() => setNotifications((prev) => ({ ...prev, periods: prev.periods.filter((_, i) => i !== index) }))}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            {notifications.periods.length > 0 && (
              <Box mt={1.5}>
                <Button variant="contained" onClick={() => saveNotifications()} disabled={saving} size="small">
                  Save settings
                </Button>
              </Box>
            )}
          </Paper>
        </Stack>
      )}

      {tab === 'security' && (
        <Stack spacing={2}>
          {/* Email verification banner */}
          {user && !user.emailVerified && (
            <Alert
              severity="warning"
              action={
                <Button size="small" onClick={async () => {
                  try {
                    await api.post('/api/auth/resend-verification');
                    setMessage('Verification email sent.');
                  } catch {
                    setMessage('Failed to send email.');
                  }
                }}>
                  Resend
                </Button>
              }
            >
              Your email is not verified. Check your inbox or resend the link.
            </Alert>
          )}

          {/* Password section: "Set" for Google-only, "Change" if already has one */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            {user?.hasPassword ? (
              <>
                <Typography variant="h6" fontWeight={900}>Change Password</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>Update your password regularly if you share this device.</Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2} sx={{ maxWidth: 520 }}>
                  <TextField type="password" label="Current password" value={passwords.currentPassword} onChange={(e) => setPasswords((p) => ({ ...p, currentPassword: e.target.value }))} />
                  <TextField type="password" label="New password" value={passwords.newPassword} onChange={(e) => setPasswords((p) => ({ ...p, newPassword: e.target.value }))} helperText="Minimum 6 characters" />
                  <Box>
                    <Button variant="contained" disabled={saving} onClick={savePassword}>Update password</Button>
                  </Box>
                </Stack>
              </>
            ) : (
              <>
                <Typography variant="h6" fontWeight={900}>Set a Password</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Your account uses Google Sign-In. Set a password to also be able to log in with email &amp; password.
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <SetPasswordForm onSuccess={() => updateUser({ hasPassword: true })} setMessage={setMessage} />
              </>
            )}
          </Paper>

          {/* Google link/unlink */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="h6" fontWeight={900}>Google Account</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {user?.googleId ? 'Your Google account is linked. You can sign in with Google.' : 'Link Google to enable one-tap sign-in.'}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {user?.googleId ? (
              <Button
                variant="outlined"
                color="error"
                disabled={googleLinking}
                onClick={async () => {
                  setGoogleLinking(true);
                  try {
                    const res = await api.post('/api/auth/google/unlink');
                    updateUser(res.data.user);
                    setMessage('Google account unlinked.');
                  } catch (err: any) {
                    setMessage(err.response?.data?.message || 'Failed to unlink.');
                  } finally {
                    setGoogleLinking(false);
                  }
                }}
              >
                Unlink Google
              </Button>
            ) : (
              <Button
                variant="outlined"
                disabled={googleLinking}
                onClick={async () => {
                  setGoogleLinking(true);
                  try {
                    const res = await api.post('/api/auth/google/link-start');
                    window.location.href = res.data.url;
                  } catch (err: any) {
                    setMessage(err.response?.data?.message || 'Failed to start Google link.');
                    setGoogleLinking(false);
                  }
                }}
                sx={{ textTransform: 'none' }}
              >
                Link Google Account
              </Button>
            )}
          </Paper>

          {/* 2FA */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="h6" fontWeight={900}>Two-Factor Authentication</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Add an extra layer of security with an authenticator app (Google Authenticator, Authy, etc.).
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {user?.twoFactorEnabled ? (
              <Stack spacing={2} sx={{ maxWidth: 360 }}>
                <Alert severity="success">2FA is enabled on your account.</Alert>
                <TextField
                  label="Enter authenticator code to disable"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value)}
                  inputProps={{ maxLength: 6 }}
                  size="small"
                />
                <Box>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={twoFASaving || twoFACode.length < 6}
                    onClick={async () => {
                      setTwoFASaving(true);
                      try {
                        await api.post('/api/auth/2fa/disable', { code: twoFACode });
                        updateUser({ twoFactorEnabled: false });
                        setTwoFACode('');
                        setMessage('2FA disabled.');
                      } catch (err: any) {
                        setMessage(err.response?.data?.message || 'Invalid code.');
                      } finally {
                        setTwoFASaving(false);
                      }
                    }}
                  >
                    Disable 2FA
                  </Button>
                </Box>
              </Stack>
            ) : !twoFAQR ? (
              <Button
                variant="outlined"
                disabled={twoFASaving}
                onClick={async () => {
                  setTwoFASaving(true);
                  try {
                    const res = await api.post('/api/auth/2fa/setup');
                    setTwoFAQR(res.data.qrCode);
                  } catch {
                    setMessage('Failed to start 2FA setup.');
                  } finally {
                    setTwoFASaving(false);
                  }
                }}
              >
                Set up 2FA
              </Button>
            ) : (
              <Stack spacing={2} sx={{ maxWidth: 360 }}>
                <Typography variant="body2">
                  Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.
                </Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <img src={twoFAQR} alt="2FA QR code" style={{ width: 180, borderRadius: 8 }} />
                </Box>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="6-digit code"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value)}
                    inputProps={{ maxLength: 6 }}
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={twoFASaving || twoFACode.length < 6}
                    onClick={async () => {
                      setTwoFASaving(true);
                      try {
                        await api.post('/api/auth/2fa/enable', { code: twoFACode });
                        updateUser({ twoFactorEnabled: true });
                        setTwoFAQR(null);
                        setTwoFACode('');
                        setMessage('2FA enabled!');
                      } catch (err: any) {
                        setMessage(err.response?.data?.message || 'Invalid code.');
                      } finally {
                        setTwoFASaving(false);
                      }
                    }}
                  >
                    Verify & Enable
                  </Button>
                </Stack>
              </Stack>
            )}
          </Paper>
        </Stack>
      )}

      <Snackbar open={Boolean(message)} autoHideDuration={3500} onClose={() => setMessage(null)} message={message} />
    </Stack>
  );
};

export default Settings;
